import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { InvestorMessagesRes } from '@dealstack/shared/lib/generated';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { type CombinedMessagesFiltersDto } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useMessages.types';
import { getMessages } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date';

// These types are required because useInfiniteQuery doesn't return the correct type when using the
// select function currently. This will be fixed when we upgrade to tanstack/react-query v5 and we
// should remove these types at that time.
type TransformedMessagesData = InfiniteData<ApiV1.Response<InvestorMessagesRes>> & {
	messages: InvestorMessagesRes['messages'];
	total: number;
	dateOfFirstMessage: Date;
};

export type UseGetMessagesReturn = ReturnType<typeof useInfiniteQuery> & {
	data: TransformedMessagesData | undefined;
};

interface Props {
	investorId: string;
	mipId: string;
	filters?: CombinedMessagesFiltersDto;
}

export const useMessages = ({ investorId, mipId, filters }: Props) => {
	const endpoint = formatUnicorn(API_PATHS.MIP_MIPID_INVESTOR_INVESTORID_MESSAGES_GET, {
		investorId,
		mipId,
	});
	const messagesArgs = {
		filters: {
			searchQuery: filters?.searchQuery,
			fieldFilter: filters?.fieldFilter,
			pagination: filters?.pagination,
			sortBy: filters?.sortBy,
		},
		investmentWorkflowId: filters?.investmentWorkflowId,
		documentWorkflowInstanceId: filters?.documentWorkflowInstanceId,
	};

	return useInfiniteQuery({
		queryKey: [endpoint, messagesArgs],
		initialPageParam: 0,
		queryFn: ({ pageParam, signal }) => getMessages(endpoint, messagesArgs, pageParam, signal),
		select: (data) => {
			const transformedPages = data.pages.map((page) => ({
				...page,
				payload: {
					...page.payload,
					messages:
						page.payload?.messages?.map((message) => ({
							...message,
							sendDate: formatDateToUI(
								new Date(message?.sendDate),
								DATE_FORMATS.DATE_TIME_ALT
							),
						})) ?? [],
					total: page.payload?.total ?? 0, // Ensure total is always a number
					dateOfFirstMessage: page.payload?.dateOfFirstMessage,
				},
			}));

			// add computed properties
			const messages = transformedPages.flatMap((page) => page.payload?.messages ?? []);
			const total = transformedPages[0]?.payload?.total ?? 0;
			const dateOfFirstMessage = transformedPages[0]?.payload?.dateOfFirstMessage;

			return {
				pages: transformedPages,
				pageParams: data.pageParams,
				messages,
				total,
				dateOfFirstMessage,
			};
		},
		getNextPageParam: (lastPage, allPages) => {
			const currentTotal = allPages.reduce(
				(acc, page) => acc + (page.payload?.messages?.length ?? 0),
				0
			);
			const total = lastPage.payload?.total ?? 0;
			return currentTotal < total ? allPages.length : undefined;
		},
	}) as UseGetMessagesReturn;
};
